import { AfterViewChecked, Component, OnInit, ViewChild } from '@angular/core';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { StudentAccountEntityComponent } from '../student-account-entity/student-account-entity.component';
import { ReportDateService } from 'src/app/services/report-date.service';

/**
 * Redeems a gift certificate as a package: looks a code up and attaches a
 * student (newly created or existing) to the certificate's pre-paid
 * enrollment. The server decides whether the certificate can still be
 * redeemed this way; this dialog only shows its answer.
 *
 * Hosts the Create Student Account form for the "new student" path so there
 * is one student-creation code path in the app.
 */
@Component({
  selector: 'app-redeem-gift-certificate-dialog',
  templateUrl: './redeem-gift-certificate-dialog.component.html',
  styleUrls: ['./redeem-gift-certificate-dialog.component.css']
})
export class RedeemGiftCertificateDialogComponent extends BaseDialogComponent implements OnInit, AfterViewChecked {
  @ViewChild(StudentAccountEntityComponent) studentForm: StudentAccountEntityComponent;

  code: string = '';
  lookupPending = false;
  certificate: any = null;
  lookupError: string = '';

  mode: 'existing' | 'new' = 'existing';
  existingStudent: string = '';
  prefillContact: any = null;

  redeeming = false;

  // Studio-timezone dates, built the way ViewTableComponent builds its own
  // rather than injected (this component sits on the dialog constructor chain).
  private _reportDateService: ReportDateService;
  get reportDateService(): ReportDateService {
    if (!this._reportDateService) {
      this._reportDateService = ReportDateService.shared(this._studioConfigService);
    }
    return this._reportDateService;
  }

  // Mirrors whether the hosted student form can be submitted. Read
  // off the @ViewChild after the view is checked, then re-checked, so the
  // actions row can bind to it without an ExpressionChangedAfterItHasBeenChecked.
  newStudentReady = false;

  /** Opened from a row's action menu: the code is known, so look it up at once. */
  override ngOnInit(): void {
    super.ngOnInit();
    const code = this._injectedDialogData?.code;
    if (code) {
      this.code = code;
      this.lookup();
    }
  }

  ngAfterViewChecked() {
    const ready = !!this.studentForm?.canSubmit;
    if (ready !== this.newStudentReady) {
      this.newStudentReady = ready;
      this._changeDetectorRef.detectChanges();
    }
  }

  lookup() {
    const code = (this.code || '').trim().toUpperCase();
    if (!code) {
      return;
    }

    this.lookupPending = true;
    this.certificate = null;
    this.lookupError = '';

    // Unscoped on the server: a certificate may be redeemed at any studio.
    this._drupalRESTService.httpGET(`/api_rest/v1/getGiftCertificateEntityFromCode?code=${encodeURIComponent(code)}`)
      .subscribe(
        (response: any) => {
          this.lookupPending = false;
          if (!response || response.error) {
            this.lookupError = 'No gift certificate was found for that code.';
            return;
          }
          this.certificate = response;
          this.prefillContact = this.contactFromRecipient(response);
        },
        () => {
          this.lookupPending = false;
          this.lookupError = 'Could not look up the gift certificate.';
        }
      );
  }

  /** Seeds the Create Student form from the certificate's recipient details. */
  private contactFromRecipient(certificate: any) {
    const name = (certificate.recipient_name || '').trim();
    const split = name.lastIndexOf(' ');
    return {
      field_first_name: split > 0 ? name.slice(0, split) : name,
      field_last_name: split > 0 ? name.slice(split + 1) : '',
      field_email: certificate.recipient_email || '',
      field_cell_phone: certificate.recipient_phone || '',
    };
  }

  get canRedeemExisting(): boolean {
    return !!this.certificate?.redeemable_as_package && !!this.regexStudentName(this.existingStudent);
  }

  get canRedeemNew(): boolean {
    return !!this.certificate?.redeemable_as_package && this.newStudentReady;
  }

  redeemExisting() {
    const studentId = this.regexStudentName(this.existingStudent);
    if (studentId) {
      this.attach(studentId);
    }
  }

  /** Creates the student through the hosted form; attach runs on its emit. */
  createStudentAndRedeem() {
    if (this.studentForm) {
      this.redeeming = true;
      this.studentForm.onSubmit(this.studentForm.f);
    }
  }

  /**
   * Releases the busy state when the hosted form fails to create the account.
   * Without this the button stayed disabled forever with nothing shown.
   */
  onStudentCreateFailed() {
    this.redeeming = false;
    this._snackBar.open('Could not create the student account. Check the form and try again.', 'Close', { duration: 6000 });
  }

  onStudentCreated(event: { studentId: string; contactId: string }) {
    if (!event?.studentId) {
      this.redeeming = false;
      this._snackBar.open('The student account was created but its id could not be read.', 'Close', { duration: 6000 });
      return;
    }
    this.attach(event.studentId);
  }

  attach(studentId: string) {
    this.redeeming = true;
    this._drupalRESTService.httpPATCH('/api_rest/v1/attachGiftCertificateStudent', {
      code: (this.code || '').trim().toUpperCase(),
      student_id: studentId,
    }).subscribe(
      (response: any) => {
        const body = response?.body ?? response;
        this.redeeming = false;
        if (!body || body.status !== 'success') {
          this._snackBar.open(body?.message || 'Could not redeem the gift certificate.', 'Close', { duration: 6000 });
          return;
        }
        this._snackBar.open(`Gift certificate ${body.code} redeemed.`, 'Close', { duration: 5000 });
        this._dialogRef.close({ redeemed: true, enrollmentId: body.enrollment_id, studentId: body.student_id });
      },
      () => {
        this.redeeming = false;
        this._snackBar.open('Could not redeem the gift certificate.', 'Close', { duration: 6000 });
      }
    );
  }
}
